// Wraps authenticated screens with the US-AUTH-02 idle-session guard.
// US-NET-04 -- offline banner when client mode and master unreachable.

import {
  giftPendingListForGuardian,
  pendingChoresList,
  pendingDepositsList,
  pendingTransfersList,
  pendingWithdrawalsList,
  routinePendingApprovals,
} from '@chorepop/core/client';
import type { Component, JSX } from 'solid-js';
import { For, Show, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { AppIcon, type AppIconName } from '../components/AppIcon';
import { ChorePopMark } from '../components/ChorePopMark';
import { useIdleSessionGuard } from '../lib/useIdleSessionGuard';
import { isMobileLayout, layoutClass } from '../state/isMobileLayout';

type Props = {
  sessionId: string;
  initialExpiresAtUtc: string;
  onSessionExpired: () => void;
  initialTab?: string;
  /**
   * Action screens are pushed above the tab shell. They deliberately use
   * their own back affordance rather than leaving the global navigation in
   * view (see docs/design/ACTION-SCREENS-SPEC.md).
   */
  showMobileNavigation?: boolean;
  /** A top-level tab can intentionally omit the overflow control. */
  showMobileMenu?: boolean;
  /** Landscape-tablet navigation rail used by the supplied Claude Design screens. */
  showTabletRail?: boolean;
  /** Optional initial displayed at the bottom of the landscape-tablet rail. */
  accountDisplayName?: string;
  children: JSX.Element;
};

type NavigationItem = {
  id: string;
  icon: AppIconName;
  label: string;
  /** Child routes use the rounder, playful SVG subset of the icon system. */
  childIcon?: AppIconName;
};

/**
 * WKWebView can pan its visual viewport down when the keyboard opens for an
 * input near the bottom of a scroll area. The visible bottom edge is therefore
 * `offsetTop + height`, not just `height`. Using height alone shortened the
 * fixed shell twice and exposed a blank strip above the keyboard.
 */
export function authenticatedViewportBottom(
  viewport: Pick<VisualViewport, 'height' | 'offsetTop'> | null | undefined,
  fallbackHeight: number,
): number {
  const height = viewport?.height ?? fallbackHeight;
  const offsetTop = Math.max(0, viewport?.offsetTop ?? 0);
  return Math.round(height + offsetTop);
}

const CHILD_NAVIGATION_EMOJI: Partial<Record<AppIconName, string>> = {
  'child-chores': '🧺',
  'child-money': '💰',
  'child-goals': '🎯',
  'child-me': '✨',
  'child-history': '📖',
  'child-settings': '⚙️',
  'child-sign-out': '👋',
};

export const AuthenticatedShell: Component<Props> = (props) => {
  useIdleSessionGuard({
    sessionId: props.sessionId,
    initialExpiresAtUtc: props.initialExpiresAtUtc,
    onExpired: () => props.onSessionExpired(),
    onForegroundLost: () => window.dispatchEvent(new Event('chorepop:session-backgrounded')),
  });

  const [menuOpen, setMenuOpen] = createSignal(false);
  const [accountMenuOpen, setAccountMenuOpen] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal(props.initialTab ?? 'home');
  const isChild = () =>
    typeof document !== 'undefined' && document.body.dataset.chorepopRole === 'child';
  // The badge is an alert, not decoration. Count the same categories the
  // approval hub can act on and render nothing when the queue is empty.
  const [pendingApprovalCount] = createResource(
    () => (!isChild() && props.showMobileNavigation === true ? props.sessionId : null),
    async (sessionId) => {
      if (!sessionId) return 0;
      const [deposits, chores, withdrawals, transfers, routines, gifts] = await Promise.all([
        pendingDepositsList({ sessionId }),
        pendingChoresList({ sessionId }),
        pendingWithdrawalsList({ sessionId }),
        pendingTransfersList({ sessionId }),
        routinePendingApprovals(sessionId),
        giftPendingListForGuardian(sessionId),
      ]);
      return [deposits, chores, withdrawals, transfers, routines, gifts].reduce(
        (total, result) => total + (result.ok ? result.value.length : 0),
        0,
      );
    },
  );

  const navigate = (tab: string) => {
    setActiveTab(tab);
    setMenuOpen(false);
    setAccountMenuOpen(false);
    window.dispatchEvent(new CustomEvent('chorepop:mobile-navigation', { detail: { tab } }));
  };
  const childEmoji = (icon: AppIconName) => CHILD_NAVIGATION_EMOJI[icon] ?? '✨';

  // Child dashboard views can also change as part of an in-app interaction
  // (notably the money-arrival animation). The dashboard, the app route and
  // the shell all consume this same event so the selected quick-navigation
  // item cannot drift away from the screen the child is actually viewing.
  onMount(() => {
    const syncChildTab = (event: Event) => {
      if (!isChild()) return;
      const tab = (event as CustomEvent<string>).detail;
      if (tab === 'chores' || tab === 'money' || tab === 'activity') {
        setActiveTab(tab);
      }
    };
    window.addEventListener('chorepop:child-home-tab', syncChildTab);
    onCleanup(() => window.removeEventListener('chorepop:child-home-tab', syncChildTab));
  });

  // WebKit can report a stale `100dvh` for the first frame after a native
  // sign-in or an app return. The next physical scroll happens to repair it,
  // which made the child header and menu appear too high until someone
  // scrolled. Keep one shared viewport-height variable in sync instead, so
  // every fixed authenticated shell starts inside the visible app viewport.
  onMount(() => {
    const visualViewport = window.visualViewport;
    let followUpFrame: number | undefined;
    const refreshViewportHeight = () => {
      const height = authenticatedViewportBottom(visualViewport, window.innerHeight);
      if (Number.isFinite(height) && height > 0) {
        document.documentElement.style.setProperty('--chorepop-app-viewport-height', `${height}px`);
      }
    };
    const refreshAfterNativeLayout = () => {
      refreshViewportHeight();
      if (followUpFrame !== undefined) cancelAnimationFrame(followUpFrame);
      followUpFrame = requestAnimationFrame(refreshViewportHeight);
    };

    refreshAfterNativeLayout();
    visualViewport?.addEventListener('resize', refreshAfterNativeLayout);
    visualViewport?.addEventListener('scroll', refreshAfterNativeLayout);
    window.addEventListener('pageshow', refreshAfterNativeLayout);
    window.addEventListener('orientationchange', refreshAfterNativeLayout);
    onCleanup(() => {
      if (followUpFrame !== undefined) cancelAnimationFrame(followUpFrame);
      visualViewport?.removeEventListener('resize', refreshAfterNativeLayout);
      visualViewport?.removeEventListener('scroll', refreshAfterNativeLayout);
      window.removeEventListener('pageshow', refreshAfterNativeLayout);
      window.removeEventListener('orientationchange', refreshAfterNativeLayout);
      document.documentElement.style.removeProperty('--chorepop-app-viewport-height');
    });
  });

  const tabs = (): NavigationItem[] =>
    isChild()
      ? [
          { id: 'chores', icon: 'basket', label: 'Chores', childIcon: 'child-chores' },
          { id: 'money', icon: 'money', label: 'Money', childIcon: 'child-money' },
          { id: 'goals', icon: 'goals', label: 'Goals', childIcon: 'child-goals' },
          { id: 'activity', icon: 'sparkles', label: 'Me', childIcon: 'child-me' },
        ]
      : [
          { id: 'home', icon: 'home', label: 'Home' },
          { id: 'approvals', icon: 'approvals', label: 'Approvals' },
          { id: 'kids', icon: 'kids', label: 'Kids' },
          { id: 'chores', icon: 'basket', label: 'Chores' },
        ];

  const overflowItems = (): NavigationItem[] =>
    isChild()
      ? [
          { id: 'money', icon: 'money', label: 'Money & buckets', childIcon: 'child-money' },
          { id: 'history', icon: 'history', label: 'History', childIcon: 'child-history' },
          { id: 'goals', icon: 'goals', label: 'Goals', childIcon: 'child-goals' },
          { id: 'settings', icon: 'settings', label: 'Settings', childIcon: 'child-settings' },
          { id: 'tutorials', icon: 'sparkles', label: 'Tutorials', childIcon: 'child-me' },
          {
            id: 'privacy',
            icon: 'lock',
            label: 'Privacy and my information',
            childIcon: 'child-me',
          },
          { id: 'report', icon: 'lock', label: 'Report unsafe content', childIcon: 'child-me' },
          { id: 'signout', icon: 'sign-out', label: 'Sign out', childIcon: 'child-sign-out' },
        ]
      : [
          { id: 'money', icon: 'money' as AppIconName, label: 'Money & buckets' },
          { id: 'routines', icon: 'routines' as AppIconName, label: 'Routines' },
          { id: 'audit', icon: 'history' as AppIconName, label: 'Activity history' },
          { id: 'settings', icon: 'settings' as AppIconName, label: 'Settings' },
          { id: 'tutorials', icon: 'sparkles' as AppIconName, label: 'Tutorials' },
          { id: 'feedback', icon: 'feedback' as AppIconName, label: 'Report a problem or idea' },
          { id: 'report', icon: 'lock' as AppIconName, label: 'Report unsafe content' },
          { id: 'signout', icon: 'sign-out' as AppIconName, label: 'Sign out' },
        ];

  const tabletTabs = (): NavigationItem[] =>
    isChild()
      ? tabs()
      : [
          ...tabs(),
          { id: 'money', icon: 'money', label: 'Money' },
          { id: 'audit', icon: 'history', label: 'Activity history' },
        ];

  return (
    <>
      <Show when={props.showTabletRail === true && !isMobileLayout()}>
        <nav class={`tablet-shell-rail layout-${layoutClass()}`} aria-label="Primary navigation">
          <ChorePopMark class="tablet-shell-rail-mark" title="ChorePop" />
          <div class="tablet-shell-rail-tabs">
            <For each={tabletTabs()}>
              {(tab) => (
                <button
                  type="button"
                  class={`tablet-shell-rail-item${activeTab() === tab.id ? ' is-active' : ''}`}
                  aria-label={tab.label}
                  aria-current={activeTab() === tab.id ? 'page' : undefined}
                  aria-pressed={activeTab() === tab.id}
                  onClick={() => navigate(tab.id)}
                >
                  <Show when={tab.childIcon} fallback={<AppIcon name={tab.icon} />}>
                    {(childIcon) => (
                      <span class="child-navigation-emoji" aria-hidden="true">
                        {childEmoji(childIcon())}
                      </span>
                    )}
                  </Show>
                  <span class="tablet-shell-rail-label">{tab.label}</span>
                </button>
              )}
            </For>
          </div>
          <div class="tablet-shell-rail-footer">
            <button
              type="button"
              class="tablet-shell-rail-settings"
              aria-label="Settings"
              onClick={() => navigate('settings')}
            >
              <AppIcon name="settings" />
            </button>
            <button
              type="button"
              class="tablet-shell-rail-avatar"
              aria-label={`${props.accountDisplayName ?? 'Account'} account menu`}
              aria-expanded={accountMenuOpen()}
              aria-haspopup="menu"
              onClick={() => setAccountMenuOpen((open) => !open)}
            >
              {(props.accountDisplayName ?? 'G').slice(0, 1).toUpperCase()}
            </button>
            <Show when={accountMenuOpen()}>
              <div class="tablet-shell-account-menu" role="menu" aria-label="Account actions">
                <Show when={!isChild()}>
                  <button type="button" role="menuitem" onClick={() => navigate('feedback')}>
                    Report a problem or idea
                  </button>
                </Show>
                <Show when={isChild()}>
                  <button type="button" role="menuitem" onClick={() => navigate('privacy')}>
                    Privacy and my information
                  </button>
                </Show>
                <button type="button" role="menuitem" onClick={() => navigate('report')}>
                  Report unsafe content
                </button>
                <button type="button" role="menuitem" onClick={() => navigate('signout')}>
                  Sign out
                </button>
              </div>
            </Show>
          </div>
        </nav>
      </Show>
      <div
        class={`authenticated-shell-content${props.showMobileNavigation !== true ? ' authenticated-shell-content--action' : ''}${props.showTabletRail === true ? ' authenticated-shell-content--tablet-rail' : ''}`}
        data-layout={layoutClass()}
      >
        {props.children}
      </div>
      <Show when={isMobileLayout() && props.showMobileNavigation === true}>
        <Show when={props.showMobileMenu !== false}>
          <button
            type="button"
            class={`mobile-shell-menu-button layout-${layoutClass()}${menuOpen() ? ' is-open' : ''}`}
            aria-label={menuOpen() ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen()}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((open) => !open)}
            data-testid="mobile-shell-menu"
          >
            <svg class="mobile-shell-menu-glyph" viewBox="0 0 24 24" aria-hidden="true">
              <Show
                when={menuOpen()}
                fallback={
                  <>
                    <path d="M4 7h16" />
                    <path d="M4 12h16" />
                    <path d="M4 17h16" />
                  </>
                }
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </Show>
            </svg>
          </button>
          <Show when={menuOpen()}>
            {/* biome-ignore lint/a11y/useFocusableInteractive: the scrim is a keyboard-dismissable modal backdrop. */}
            <div
              class="mobile-shell-menu-scrim"
              role="button"
              tabindex={0}
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
                  setMenuOpen(false);
                }
              }}
            >
              <nav
                class="mobile-shell-overflow"
                aria-label="More navigation"
                onClick={(event) => event.stopPropagation()}
              >
                <For each={overflowItems()}>
                  {(item) => (
                    <button
                      type="button"
                      class="mobile-shell-overflow-row"
                      data-testid={item.id === 'feedback' ? 'guardian-menu-feedback' : undefined}
                      onClick={() => navigate(item.id)}
                    >
                      <Show when={item.childIcon} fallback={<AppIcon name={item.icon} />}>
                        {(childIcon) => (
                          <span class="child-navigation-emoji" aria-hidden="true">
                            {childEmoji(childIcon())}
                          </span>
                        )}
                      </Show>
                      {item.label}
                    </button>
                  )}
                </For>
              </nav>
            </div>
          </Show>
        </Show>
        <nav class={`mobile-bottom-nav layout-${layoutClass()}`} aria-label="Primary navigation">
          <For each={tabs()}>
            {(tab) => (
              <button
                type="button"
                class={`mobile-bottom-nav-item${activeTab() === tab.id ? ' is-active' : ''}`}
                aria-current={activeTab() === tab.id ? 'page' : undefined}
                aria-pressed={activeTab() === tab.id}
                onClick={() => navigate(tab.id)}
                data-testid={`${isChild() ? 'child-nav' : 'mobile-nav'}-${tab.id}`}
              >
                <span class="mobile-bottom-nav-icon" aria-hidden="true">
                  <Show when={tab.childIcon} fallback={<AppIcon name={tab.icon} />}>
                    {(childIcon) => (
                      <span class="child-navigation-emoji" aria-hidden="true">
                        {childEmoji(childIcon())}
                      </span>
                    )}
                  </Show>
                  <Show when={tab.id === 'approvals' && (pendingApprovalCount() ?? 0) > 0}>
                    <span class="mobile-bottom-nav-badge" aria-hidden="true">
                      {pendingApprovalCount()}
                    </span>
                  </Show>
                </span>
                <span>{tab.label}</span>
              </button>
            )}
          </For>
        </nav>
      </Show>
    </>
  );
};
